import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Search, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { reciterAPI, recitationAPI, surahAPI } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url.replace('/uploads/', '/api/files/')}`;
};

const ReciterPlayer = () => {
  const { reciterId } = useParams();
  const navigate = useNavigate();
  const [reciter, setReciter] = useState(null);
  const [recitations, setRecitations] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReciterModal, setShowReciterModal] = useState(false);
  const [allReciters, setAllReciters] = useState([]);
  const [reciterSearchTerm, setReciterSearchTerm] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef(null);

  useEffect(() => {
    loadReciterData();
  }, [reciterId]);

  const loadReciterData = async () => {
    setLoading(true);
    try {
      // Load reciter details
      const reciterRes = await reciterAPI.getOne(reciterId);
      setReciter(reciterRes.data);

      // Load recitations for this reciter
      const recitationsRes = await recitationAPI.getByReciter(reciterId);
      const recitationsData = recitationsRes.data;

      // Load all surahs to get names
      const surahsRes = await surahAPI.getAll();
      const surahsMap = {};
      surahsRes.data.forEach(surah => {
        surahsMap[surah.id] = surah;
      });

      // Enrich recitations with surah names
      const enrichedRecitations = recitationsData.map(rec => ({
        ...rec,
        surahName: surahsMap[rec.surahId]?.name || 'Bilinmeyen',
        surahOrder: surahsMap[rec.surahId]?.order || 999,
        audioUrl: getFileUrl(rec.audioUrl)
      }));

      // Sort by surah order
      enrichedRecitations.sort((a, b) => a.surahOrder - b.surahOrder);

      setRecitations(enrichedRecitations);

      if (enrichedRecitations.length > 0) {
        setCurrentTrack(enrichedRecitations[0]);
      }
    } catch (error) {
      console.error('Error loading reciter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTrackChange = (track) => {
    setCurrentTrack(track);
  };

  const handlePlaySurah = (recitation) => {
    if (currentTrack?.id === recitation.id) {
      // Toggle play/pause for same track
      togglePlayPause();
    } else {
      // Switch to new track
      setCurrentTrack(recitation);
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const togglePlayPause = async () => {
    if (audioRef.current) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Audio playback error:', error);
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleEnded = () => {
    // Auto play next track
    const currentIndex = recitations.findIndex(r => r.id === currentTrack?.id);
    if (currentIndex >= 0 && currentIndex < recitations.length - 1) {
      const nextTrack = recitations[currentIndex + 1];
      setCurrentTrack(nextTrack);
      setCurrentTime(0);
      // Will auto-play when audio loads
    } else {
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current && !isNaN(newTime)) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleSeekStart = () => {
    // Pause during seeking for better UX
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
    }
  };

  const handleSeekEnd = () => {
    // Resume if was playing
    if (isPlaying && audioRef.current) {
      audioRef.current.play().catch(err => console.error('Play error:', err));
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-play when track changes
  React.useEffect(() => {
    if (currentTrack && audioRef.current) {
      audioRef.current.load();
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(error => {
        console.error('Auto-play failed:', error);
        setIsPlaying(false);
      });
    }
  }, [currentTrack]);

  const loadAllReciters = async () => {
    try {
      const response = await reciterAPI.getAll();
      const recitersData = response.data || [];
      
      // Load recitation count and total duration for each reciter
      const recitersWithStats = await Promise.all(
        recitersData.map(async (reciter) => {
          try {
            const recitationsRes = await recitationAPI.getByReciter(reciter.id);
            const recitationsList = recitationsRes.data || [];
            
            // Calculate total duration
            const totalSeconds = recitationsList.reduce((sum, rec) => {
              if (rec.duration) {
                const parts = rec.duration.split(':');
                const minutes = parseInt(parts[0]) || 0;
                const seconds = parseInt(parts[1]) || 0;
                return sum + (minutes * 60) + seconds;
              }
              return sum;
            }, 0);
            
            const totalMinutes = Math.floor(totalSeconds / 60);
            const totalHours = Math.floor(totalMinutes / 60);
            const remainingMinutes = totalMinutes % 60;
            const totalDuration = totalHours > 0 
              ? `${totalHours} saat ${remainingMinutes} dk`
              : `${totalMinutes} dk`;
            
            return {
              ...reciter,
              recitationCount: recitationsList.length,
              totalDuration: totalDuration
            };
          } catch (error) {
            return { ...reciter, recitationCount: 0, totalDuration: '0 dk' };
          }
        })
      );
      
      setAllReciters(recitersWithStats);
    } catch (error) {
      console.error('Error loading all reciters:', error);
    }
  };

  const handleOpenReciterModal = () => {
    loadAllReciters();
    setShowReciterModal(true);
  };

  const handleReciterChange = (newReciterId) => {
    setShowReciterModal(false);
    navigate(`/hafizlar/${newReciterId}`);
  };

  const filteredModalReciters = allReciters.filter(r =>
    r.name.toLowerCase().includes(reciterSearchTerm.toLowerCase()) ||
    r.country.toLowerCase().includes(reciterSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F9F6F1]">
        <p className="text-[#8B7355]">Yükleniyor...</p>
      </div>
    );
  }

  if (!reciter) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F9F6F1]">
        <p className="text-[#8B7355]">Hafız bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F1] pb-32">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/hafizlar">
          <Button variant="ghost" className="mb-6 text-[#6B5744] hover:bg-[#E8DCC8]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Hafızlara Dön
          </Button>
        </Link>

        {/* Reciter Header */}
        <Card className="bg-white border-[#E8DCC8] p-8 mb-8">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <img
                src={getFileUrl(reciter.image)}
                alt={reciter.name}
                className="h-32 w-32 rounded-full object-cover border-4 border-[#D5C4A1]"
              />
            </div>

            <div className="flex-1">
              <h1 className="text-3xl font-bold text-[#5C4033] mb-2">{reciter.name}</h1>
              <div className="flex items-center gap-2 mb-2">
                {reciter.flagUrl && (
                  <img 
                    src={reciter.flagUrl.startsWith('http') ? reciter.flagUrl : `${process.env.REACT_APP_BACKEND_URL}${reciter.flagUrl.replace('/uploads/', '/api/files/')}`} 
                    alt={`${reciter.country} Bayrağı`} 
                    className="w-8 h-6 object-cover rounded"
                  />
                )}
                <p className="text-[#8B7355]">{reciter.country}</p>
              </div>
              <p className="text-sm text-[#8B7355]">
                <span className="font-semibold">{recitations.length}</span> ses kaydı
              </p>
            </div>

            {/* Change Reciter Button with Animation */}
            <Button
              onClick={handleOpenReciterModal}
              variant="outline"
              className="relative text-[#8B7355] border-2 border-[#D5C4A1] hover:bg-[#F5EFE7] hover:border-[#8B7355] hover:shadow-[0_0_15px_rgba(139,115,85,0.6)] transition-all duration-300 hover:scale-105 animate-pulse"
            >
              Hafız Değiştir
            </Button>
          </div>
        </Card>

        {/* Surah List */}
        <div>
          <h2 className="text-2xl font-bold text-[#5C4033] mb-4">Sure Listesi</h2>
          <div className="space-y-3">
            {recitations.map((recitation, index) => {
              const isCurrentTrack = currentTrack?.id === recitation.id;
              return (
                <Card
                  key={recitation.id}
                  className={`bg-white border-[#E8DCC8] hover:border-[#D5C4A1] p-4 group transition-all ${
                    isCurrentTrack ? 'border-[#8B7355] bg-[#F5EFE7] shadow-lg' : ''
                  }`}
                >
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => handlePlaySurah(recitation)}>
                    <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                      {/* Play Icon - Always Visible */}
                      <button className="w-10 h-10 sm:w-12 sm:h-12 bg-[#8B7355] hover:bg-[#6B5744] border-2 border-[#D5C4A1] rounded-full flex items-center justify-center transition-all hover:scale-110 flex-shrink-0">
                        {isCurrentTrack && isPlaying ? (
                          <Pause className="h-5 w-5 sm:h-6 sm:w-6 text-white fill-white" />
                        ) : (
                          <Play className="h-5 w-5 sm:h-6 sm:w-6 text-white fill-white ml-0.5" />
                        )}
                      </button>

                      {/* Surah Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-lg font-semibold text-[#5C4033] group-hover:text-[#8B7355] truncate">
                          {recitation.surahName}
                        </h3>
                        <p className="text-xs sm:text-sm text-[#8B7355]">
                          Süre: {recitation.duration || '00:00'}
                        </p>
                      </div>
                    </div>

                    {/* Recitation Number (Index-based) */}
                    <div className="text-right">
                      <div className="flex flex-col items-center">
                        <span className="text-3xl font-bold text-[#8B7355]">
                          {index + 1}
                        </span>
                        <span className="text-xs text-[#B8A894]">
                          Tilavet
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Inline Audio Player - Show when playing */}
                  {isCurrentTrack && (
                    <div className="mt-4 pt-4 border-t border-[#E8DCC8]">
                      <div className="space-y-3">
                        {/* Progress Bar */}
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-[#8B7355] font-mono min-w-[40px]">
                            {formatTime(currentTime)}
                          </span>
                          <div className="flex-1 relative h-6 flex items-center">
                            {/* Background track */}
                            <div className="absolute w-full h-2 bg-[#E8DCC8] rounded-full" />
                            {/* Progress fill */}
                            <div 
                              className="absolute h-2 bg-gradient-to-r from-[#8B7355] to-[#A68968] rounded-full pointer-events-none transition-all duration-150"
                              style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
                            />
                            {/* Input slider */}
                            <input
                              type="range"
                              min="0"
                              max={duration || 0}
                              step="0.1"
                              value={currentTime || 0}
                              onChange={handleSeek}
                              onMouseDown={handleSeekStart}
                              onMouseUp={handleSeekEnd}
                              onTouchStart={handleSeekStart}
                              onTouchEnd={handleSeekEnd}
                              className="relative w-full h-2 bg-transparent appearance-none cursor-pointer z-10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#8B7355] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:transition-transform [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#8B7355] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md"
                            />
                          </div>
                          <span className="text-xs text-[#8B7355] font-mono min-w-[40px]">
                            {formatTime(duration)}
                          </span>
                        </div>

                        {/* Now Playing Indicator */}
                        <div className="flex items-center justify-center gap-2 text-sm">
                          {isPlaying ? (
                            <>
                              <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                              <span className="text-green-600 font-medium">Şu an çalıyor</span>
                            </>
                          ) : (
                            <>
                              <span className="inline-block w-2 h-2 bg-gray-400 rounded-full"></span>
                              <span className="text-gray-500">Duraklatıldı</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {recitations.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#8B7355]">Bu hafız için henüz sure kaydı bulunmamaktadır.</p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden Audio Element */}
      {currentTrack && (
        <audio
          ref={audioRef}
          src={currentTrack.audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onLoadedMetadata={handleTimeUpdate}
          className="hidden"
        />
      )}

      {/* Reciter Change Modal */}
      <Dialog open={showReciterModal} onOpenChange={setShowReciterModal}>
        <DialogContent className="max-w-2xl bg-[#F9F6F1]">
          <style dangerouslySetInnerHTML={{__html: `
            [role="dialog"] > button[type="button"]:first-of-type { display: none !important; }
          `}} />
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#5C4033]">Hafız Seçin</h2>
              <p className="text-sm text-[#8B7355] mt-1">Farklı bir hafızın sesini dinlemek için seçim yapın</p>
            </div>
            <button
              onClick={() => setShowReciterModal(false)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white hover:bg-[#E8DCC8] border border-[#D5C4A1] hover:border-[#8B7355] transition-all shadow-sm hover:shadow-md flex-shrink-0"
            >
              <X className="h-4 w-4 text-[#8B7355]" />
              <span className="text-[#8B7355] font-medium text-xs">Kapat</span>
            </button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B7355] h-5 w-5" />
              <Input
                type="text"
                placeholder="Hafız ara..."
                value={reciterSearchTerm}
                onChange={(e) => setReciterSearchTerm(e.target.value)}
                className="pl-10 bg-white border-[#D5C4A1] text-[#5C4033] placeholder:text-[#B8A894]"
              />
            </div>
          </div>

          {/* Reciter List */}
          <div className="space-y-3">
            {filteredModalReciters.map((r) => (
              <Card
                key={r.id}
                className={`p-4 border-[#E8DCC8] hover:border-[#D5C4A1] ${
                  r.id === reciterId ? 'bg-[#F5EFE7] border-[#8B7355]' : 'bg-white'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Profile Image */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={getFileUrl(r.image)}
                      alt={r.name}
                      className="h-16 w-16 rounded-full object-cover border-2 border-[#D5C4A1]"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-[#5C4033] mb-1">{r.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-[#8B7355] mb-1">
                      {r.flagUrl && (
                        <img 
                          src={r.flagUrl.startsWith('http') ? r.flagUrl : `${process.env.REACT_APP_BACKEND_URL}${r.flagUrl.replace('/uploads/', '/api/files/')}`} 
                          alt={`${r.country} Bayrağı`} 
                          className="w-5 h-4 object-cover rounded"
                        />
                      )}
                      <span>{r.country}</span>
                    </div>
                    <div className="text-sm text-[#8B7355] space-y-1 mt-2">
                      <div className="flex items-center gap-2 bg-[#F5EFE7] px-2 py-1 rounded">
                        <span className="font-semibold text-[#5C4033]">{r.recitationCount || 0}</span> 
                        <span>ses kaydı</span>
                        <span className="mx-1">•</span>
                        <span className="font-semibold text-[#5C4033]">{r.totalDuration || '0 dk'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Select Button */}
                  {r.id === reciterId ? (
                    <div className="flex items-center gap-2 text-[#8B7355] font-semibold bg-[#E8DCC8] px-4 py-2 rounded-lg">
                      ✓ Seçili
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleReciterChange(r.id)}
                      className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
                    >
                      Seç
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {filteredModalReciters.length === 0 && (
            <div className="text-center py-8 text-[#8B7355]">
              Aradığınız hafız bulunamadı.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReciterPlayer;
